import { useCallback } from "react";
import { useCloneStrategy, type CloneResponse, type CloneStrategyRequest } from "@repo/api";
import { useBusy, useToast, useTranslation } from "@repo/core";
import { useNavigate } from "react-router-dom";
import { usePortfolioQueryModifications } from "./usePortfolioQueryModifications";

export const usePortfolioActions = () => {
  const navigate = useNavigate();
  const { setBusy } = useBusy();
  const { addSuccessMessage, addErrorMessage } = useToast();
  const { tStrategy } = useTranslation();
  const { mutate: cloneStrategy, isPending: isCloning } = useCloneStrategy();
  const { emptyData, refetchData, flushData } =
    usePortfolioQueryModifications();

  const clonePortfolio = useCallback(
    ({
      payload,
      busyMessage,
      successMessage,
      errorMessage,
      preventErrorMessage = false,
      preventNavigate = false,
      customSuccess,
      customError,
    }: {
      payload: CloneStrategyRequest;
      busyMessage: string;
      successMessage: string;
      errorMessage: string;
      preventErrorMessage?: boolean;
      preventNavigate?: boolean;
      customSuccess?: (response: CloneResponse) => void;
      customError?: (error: unknown) => void;
    }) => {
      setBusy(true, busyMessage);
      cloneStrategy(payload, {
        onSuccess: (response: CloneResponse) => {
          setBusy(false); // Hide busy indicator
          if (successMessage) {
            addSuccessMessage(successMessage);
          }
          flushData();
          if (customSuccess) {
            customSuccess(response);
          }
          if (!preventNavigate) {
            navigate(`/${response.modelId}`);
          }
        },
        onError: (error: unknown) => {
          setBusy(false); // Hide busy indicator on error
          if (!preventErrorMessage) {
            addErrorMessage(errorMessage);
          }
          console.error("Error cloning strategy:", error);

          if (customError) {
            customError(error);
          }
        },
      });
    },
    [
      addErrorMessage,
      addSuccessMessage,
      cloneStrategy,
      navigate,
      setBusy,
      flushData,
    ]
  );

  const addToOverview = useCallback(
    ({
      payload,
      preventErrorMessage = false,
      customSuccess,
      customError,
    }: {
      payload: CloneStrategyRequest;
      preventErrorMessage?: boolean;
      customSuccess?: (response: CloneResponse) => void;
      customError?: (error: unknown) => void;
    }) => {
      clonePortfolio({
        payload,
        busyMessage: tStrategy("actions.addToOverview.busy"),
        successMessage: tStrategy("actions.addToOverview.success"),
        errorMessage: tStrategy("actions.addToOverview.error"),
        preventErrorMessage,
        customSuccess,
        customError,
      });
    },
    [clonePortfolio, tStrategy]
  );

  const saveAsActive = useCallback(
    ({
      payload,
    }: {
      payload: CloneStrategyRequest;
      preventErrorMessage?: boolean;
      customSuccess?: (response: CloneResponse) => void;
      customError?: (error: unknown) => void;
    }) => {
      clonePortfolio({
        payload,
        successMessage: tStrategy("actions.saveAsActive.success"),
        errorMessage: tStrategy("actions.saveAsActive.error"),
        busyMessage: tStrategy("actions.saveAsActive.busy"),
        customSuccess: (response) => {
          const id = response.modelId;
          flushData(payload.modelId);
          emptyData(id);
          refetchData(id);
        },
      });
    },
    [clonePortfolio, emptyData, refetchData, flushData, tStrategy]
  );

  return {
    addToOverview,
    saveAsActive,
    clonePortfolio,
    isCloning,
  };
};
